/**
 * Chakra UI v3 Theme Configuration
 *
 * This is THE theme file for Showpass. All colors adapt to dark mode.
 *
 * ## Architecture
 *
 * ```
 * colors.ts    → colorModes (raw values) + brandSemanticTokens (shared)
 * theme.ts     → THE theme (this file) - creates Chakra system
 * ```
 *
 * ## Key Principle
 *
 * EVERY color adapts to dark mode. There are no static palette tokens.
 * Use `gray.500`, `primary.500`, etc. and they automatically switch.
 *
 * ## SSR/Hydration
 *
 * `defaultSystem` is created once at module load and exported.
 * This prevents style flash on hydration.
 */
import {
  createSystem,
  defaultConfig,
  defineConfig,
  defineSlotRecipe,
} from "@chakra-ui/react";
import {
  colorModes,
  brandSemanticTokens,
  generateFullPalette,
  generateBgSubtleToken,
} from "./props/colors";
import { spacingTokens } from "./props/spacing";
import { radiiTokens } from "./props/radii";
import breakpoints, { breakpointsTheme } from "./props/breakpoints";
import { textStyles, fontSizeTokens } from "./props/textStyles";
import checkboxSlotRecipe from "./components/Checkbox";
import linkRecipe from "./components/Link";
import tagSlotRecipe from "./components/Tag";
import { ColorPalette } from "./types";

// =============================================================================
// STATIC COLOR TOKENS (truly static - don't change with mode)
// Only for absolute colors and third-party brand colors
// =============================================================================

const staticColors = {
  white: { value: "#FFFFFF" },
  black: { value: "#000000" },
  transparent: { value: "transparent" },
  // Third-party brand colors (truly static, never change)
  spotify: {
    green: { value: "#1db954" },
    black: { value: "#191414" },
  },
  stripe: {
    fontDark: { value: "#11142D" },
    gray400: { value: "#D1D5DB" },
    gray500: { value: "#9CA3AF" },
    gray600: { value: "#6B7280" },
    primary600: { value: "#0F4D52" },
    primary800: { value: "#073336" },
    secondary600: { value: "#CA1E1E" },
    red600: { value: "#EF4444" },
  },
};

// Raw hex exports for third-party APIs (Stripe JS, QR code lib)
// Stripe elements use consistent colors regardless of app color mode
export function getStripeColors(_mode: "light" | "dark" = "light"): {
  fontDark: string;
  gray400: string;
  gray500: string;
  gray600: string;
  primary600: string;
  primary800: string;
  secondary600: string;
  red600: string;
} {
  return {
    fontDark: "#11142D",
    gray400: "#D1D5DB",
    gray500: "#9CA3AF",
    gray600: "#6B7280",
    primary600: "#0F4D52",
    primary800: "#073336",
    secondary600: "#CA1E1E",
    red600: "#EF4444",
  };
}

// Backward compatibility: default export for light mode
export const STRIPE_COLORS = getStripeColors("light");

export const QR_CODE_COLORS = {
  foreground: staticColors.black.value,
  background: staticColors.white.value,
};

// =============================================================================
// SEMANTIC COLOR TOKENS
// ALL colors adapt to light/dark mode - no exceptions
// =============================================================================

const semanticTokens = {
  colors: {
    // === ALL COLOR PALETTES (numbered shades + semantic tokens) ===
    // Each palette gets both 100-950 shades AND .solid, .muted, .emphasized, etc.
    gray: generateFullPalette(colorModes.gray),
    red: generateFullPalette(colorModes.red),
    green: generateFullPalette(colorModes.green),
    yellow: generateFullPalette(colorModes.yellow),
    blue: generateFullPalette(colorModes.blue),
    purple: generateFullPalette(colorModes.purple),
    pink: generateFullPalette(colorModes.pink),
    indigo: generateFullPalette(colorModes.indigo),

    // === BRAND COLORS (CANNOT be overridden via generateTheme) ===
    // Use only for Showpass branding elements that should not change with custom themes,
    // otherwise always use primary or secondary.
    brandPrimary: generateFullPalette(colorModes.brandPrimary),
    brandSecondary: generateFullPalette(colorModes.brandSecondary),

    // === PRIMARY (dynamic - can be overridden via generateTheme) ===
    primary: generateFullPalette(colorModes.primary),
    ...generateBgSubtleToken(colorModes.primary, "primary"),

    // === SECONDARY (dynamic - can be overridden via generateTheme) ===
    secondary: generateFullPalette(colorModes.secondary),
    ...generateBgSubtleToken(colorModes.secondary, "secondary"),

    // === BASE SEMANTIC COLORS ===
    font: {
      value: { _light: colorModes.font.light, _dark: colorModes.font.dark },
    },
    "font.default": {
      value: { _light: colorModes.font.light, _dark: colorModes.font.dark },
    },
    charcoal: {
      value: {
        _light: colorModes.charcoal.light,
        _dark: colorModes.charcoal.dark,
      },
    },
    midnight: {
      value: {
        _light: colorModes.midnight.light,
        _dark: colorModes.midnight.dark,
      },
    },
    offWhite: {
      value: {
        _light: colorModes.offWhite.light,
        _dark: colorModes.offWhite.dark,
      },
    },
    "logo.fill": {
      value: { _light: colorModes.secondary[500].light, _dark: "#FFFFFF" },
    },

    // === BACKGROUNDS ===
    bg: {
      value: { _light: colorModes.white.light, _dark: colorModes.white.dark },
    },
    "bg.subtle": {
      value: {
        _light: colorModes.offWhite.light,
        _dark: colorModes.offWhite.dark,
      },
    },
    "bg.muted": {
      value: {
        _light: colorModes.gray[100].light,
        _dark: colorModes.gray[100].dark,
      },
    },
    "bg.emphasized": {
      value: {
        _light: colorModes.gray[200].light,
        _dark: colorModes.gray[200].dark,
      },
    },
    "bg.inverted": {
      value: {
        _light: colorModes.gray[900].light,
        _dark: colorModes.gray[900].dark,
      },
    },

    // === FOREGROUNDS (TEXT) ===
    fg: {
      value: { _light: colorModes.font.light, _dark: colorModes.font.dark },
    },
    "fg.muted": {
      value: {
        _light: colorModes.gray[600].light,
        _dark: colorModes.gray[600].dark,
      },
    },
    "fg.subtle": {
      value: {
        _light: colorModes.gray[500].light,
        _dark: colorModes.gray[500].dark,
      },
    },
    "fg.inverted": {
      value: { _light: colorModes.white.light, _dark: colorModes.font.light },
    },

    // === LEGACY FONT TOKENS ===
    "font.dark": {
      value: { _light: colorModes.font.light, _dark: colorModes.font.dark },
    },
    "font.light": {
      value: { _light: "#FFFFFF", _dark: colorModes.font.light },
    },

    // === BORDERS ===
    border: {
      value: {
        _light: colorModes.gray[300].light,
        _dark: colorModes.gray[300].dark,
      },
    },
    "border.default": {
      value: {
        _light: colorModes.gray[300].light,
        _dark: colorModes.gray[300].dark,
      },
    },
    "border.muted": {
      value: {
        _light: colorModes.gray[200].light,
        _dark: colorModes.gray[200].dark,
      },
    },
    "border.emphasized": {
      value: {
        _light: colorModes.gray[500].light,
        _dark: colorModes.gray[500].dark,
      },
    },

    // === STATUS COLORS ===
    ...brandSemanticTokens,
  },
};

// =============================================================================
// THEME CONFIG
// =============================================================================

const accordionSlotRecipe = defineSlotRecipe({
  className: "accordion",
  slots: [
    "root",
    "item",
    "itemTrigger",
    "itemContent",
    "itemBody",
    "itemIndicator",
  ],
  base: {
    itemContent: { padding: 0 },
    itemBody: { padding: 0 },
  },
});

// =============================================================================
// SIZE TOKENS
//
// Named tokens (xs, sm, md, lg, xl) = BREAKPOINTS (for maxW, w, containers)
// Element tokens (element.xs, element.sm) = SMALL FIXED SIZES (icons, buttons)
// =============================================================================
const sizeTokens = {
  // Chakra default sizes (preserve existing behavior)
  // maxW="sm" = 384px, maxW="lg" = 512px
  xs: { value: "20rem" }, // 320px
  sm: { value: "24rem" }, // 384px
  md: { value: "28rem" }, // 448px
  lg: { value: "32rem" }, // 512px
  xl: { value: "36rem" }, // 576px
  "2xl": { value: "42rem" }, // 672px
  "3xl": { value: "48rem" }, // 768px
  "4xl": { value: "56rem" }, // 896px
  "5xl": { value: "64rem" }, // 1024px
  "6xl": { value: "72rem" }, // 1152px
  "7xl": { value: "80rem" }, // 1280px
  "8xl": { value: "90rem" }, // 1440px

  // Container sizes (match breakpoints for layouts)
  // maxW="container.lg" = 1200px
  "container.xs": { value: `${breakpoints.xs}px` }, // 576px
  "container.sm": { value: `${breakpoints.sm}px` }, // 768px
  "container.md": { value: `${breakpoints.md}px` }, // 1024px
  "container.lg": { value: `${breakpoints.lg}px` }, // 1200px
  "container.xl": { value: `${breakpoints.xl}px` }, // 1440px
  "container.content": { value: "1520px" }, // Standard content width
  "container.2xl": { value: `${breakpoints["2xl"]}px` }, // 1600px
  "container.3xl": { value: `${breakpoints["3xl"]}px` }, // 1880px

  // Element sizes (for icons, buttons, avatars, small fixed elements)
  // boxSize="element.md" = 16px
  "element.2xs": { value: "4px" },
  "element.xs": { value: "8px" },
  "element.sm": { value: "12px" },
  "element.md": { value: "16px" },
  "element.lg": { value: "20px" },
  "element.xl": { value: "24px" },
  "element.2xl": { value: "32px" },
  "element.3xl": { value: "40px" },
  "element.4xl": { value: "48px" },
  "element.5xl": { value: "64px" },
  // TODO: Check why these are not defined in the design tokens in figma (80, 100, 128, 160)
  // Added these to remove the warnings for now until we have more time to diagnose the issue.
  // There are also other sizes that are not defined in the design tokens in figma (50, 100, 140, 128, 160)
  "element.6xl": { value: "80px" },
  "element.7xl": { value: "100px" },
  "element.8xl": { value: "128px" },
  "element.9xl": { value: "160px" },
  "element.10xl": { value: "180px" },
  "element.11xl": { value: "200px" },
  "element.12xl": { value: "220px" },
  "element.13xl": { value: "240px" },

  // Special values
  none: { value: "none" },
  0: { value: "0px" },
  full: { value: "100%" },
  min: { value: "min-content" },
  max: { value: "max-content" },
  fit: { value: "fit-content" },
};
const tableSlotRecipe = defineSlotRecipe({
  className: "table",
  slots: ["root", "header", "body", "row", "columnHeader", "cell", "footer"],
  base: {
    root: {
      fontVariantNumeric: "lining-nums tabular-nums",
      borderCollapse: "collapse",
      width: "full",
      textAlign: "start",
      verticalAlign: "top",
    },
    row: {
      _selected: {
        bg: "colorPalette.subtle",
      },
    },
    columnHeader: {
      fontWeight: "medium",
      textAlign: "start",
      color: "fg",
    },
    cell: {
      textAlign: "start",
      verticalAlign: "middle",
    },
  },
  variants: {
    variant: {
      // Custom "simple" variant: gray header, row borders, no outer border
      simple: {
        columnHeader: {
          bg: "bg.subtle",
          borderBottom: "1px solid",
          borderColor: "border",
        },
        cell: {
          borderBottom: "1px solid",
          borderColor: "border",
          bg: "bg",
          color: "fg",
        },
      },
      unstyled: {
        root: {
          // 1. Reset standard table properties
          border: "none",
          borderCollapse: "collapse",

          // 2. Force child elements to have zero borders using nested selectors
          // This overrides any specific 'cell' or 'row' base styles
          "& tr, & th, & td": {
            border: "none !important",
            borderBottom: "none !important",
            borderBottomWidth: "0px !important",
            boxShadow: "none !important",
            background: "transparent",
          },
        },
        // 3. Explicitly nullify the slots just in case
        header: { bg: "transparent", border: "none" },
        body: { bg: "transparent", border: "none" },
        row: { bg: "transparent", border: "none" },
        columnHeader: { bg: "transparent", border: "none" },
        cell: { bg: "transparent", border: "none" },
      },
    },
  },
});

// Shared text selection styles used by both default and custom themes
const selectionStyles = {
  "::selection": {
    backgroundColor: "revert",
    color: "revert",
  },
  "input::selection, textarea::selection": {
    backgroundColor: "revert",
    color: "revert",
  },
};

const baseConfig = defineConfig({
  theme: {
    keyframes: {
      shimmer: {
        "0%": { transform: "translateX(-100%)" },
        "100%": { transform: "translateX(100%)" },
      },
      calendarAnimation: {
        "0%, 100%": { opacity: 1 },
        "50%": { opacity: 0.5 },
      },
      slideDown: {
        "0%": { transform: "translateY(-50%)", opacity: 0 },
        "100%": { transform: "translateY(0)", opacity: 1 },
      },
      qrScanMove: {
        "0%, 5%, 100%": { top: "calc(var(--qr-spacing) * -1)" },
        "50%": { top: "calc(var(--qr-size) + var(--qr-spacing))" },
      },
      qrScanGradient: {
        "0%": { background: "var(--qr-color)" },
        "20%": {
          background: "linear-gradient(to top, var(--qr-color), transparent)",
        },
        "50%": { background: "var(--qr-color)" },
        "65%, 70%": {
          background:
            "linear-gradient(to bottom, var(--qr-color), transparent)",
        },
        "100%": { background: "var(--qr-color)" },
      },
      qrScanHeight: {
        "0%, 50%, 100%": { height: "2px" },
        "20%, 65%, 70%": { height: "20px" },
      },
      slideLeft: {
        "0%": { transform: "translateX(100%)", opacity: 0 },
        "100%": { transform: "translateX(0)", opacity: 1 },
      },
      slideRight: {
        "0%": { transform: "translateX(-100%)", opacity: 0 },
        "100%": { transform: "translateX(0)", opacity: 1 },
      },
    },
    tokens: {
      // Only truly static colors (white, black, third-party brands)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      colors: staticColors as any,
      fonts: {
        heading: { value: "Lato" },
        body: { value: "Lato" },
      },
      fontSizes: fontSizeTokens,
      spacing: spacingTokens,
      sizes: sizeTokens, // Uses breakpoint values: sm=768px, md=1024px, lg=1200px
      radii: radiiTokens,
      animations: { textShimmer: { value: "shimmer 2s linear infinite" } },
    },
    // ALL colors come from semantic tokens - they ALL adapt to dark mode
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    semanticTokens: semanticTokens as any,
    // Text styles for consistent typography (use textStyle="body.md" etc.)
    textStyles,
    breakpoints: breakpointsTheme,
    recipes: {
      link: linkRecipe,
    },
    slotRecipes: {
      accordion: accordionSlotRecipe,
      table: tableSlotRecipe,
      checkbox: checkboxSlotRecipe,
      tag: tagSlotRecipe,
    },
  },
  globalCss: {
    ":root": { "--100dvh": "90vh" },
    "@supports (height: 100dvh)": {
      ":root": { "--100dvh": "100dvh" },
    },
    body: {
      color: "fg",
      fontSize: "md", // 16px - default for all text
    },
    ...selectionStyles,
  },
} as Parameters<typeof defineConfig>[0]);

// =============================================================================
// EXPORTS
// =============================================================================

// DEFAULT SYSTEM - Created once, same object for SSR and client
export const defaultSystem = createSystem(defaultConfig, baseConfig);

// For dynamic theming (theme-primary/theme-secondary query params)
// Creates semantic tokens from custom color objects with light/dark variants
/**
 * Generate a custom theme with overridden primary/secondary colors.
 * Uses the same generateFullPalette() function as the default theme
 * to ensure consistent token structure.
 */
export default function generateTheme(opts: {
  secondaryColor?: ColorPalette;
  primaryColor?: ColorPalette;
}) {
  if (!opts.primaryColor && !opts.secondaryColor) {
    return defaultSystem;
  }

  // Build custom semantic tokens using the SAME generators as default theme
  const customSemanticTokens = {
    colors: {
      ...semanticTokens.colors,
      // Override primary palette with ALL tokens (numbered + semantic)
      ...(opts.primaryColor && {
        primary: generateFullPalette(opts.primaryColor),
        ...generateBgSubtleToken(opts.primaryColor, "primary"),
      }),
      // Override secondary palette with ALL tokens (numbered + semantic)
      ...(opts.secondaryColor && {
        secondary: generateFullPalette(opts.secondaryColor),
        ...generateBgSubtleToken(opts.secondaryColor, "secondary"),
      }),
    },
  };

  const customConfig = defineConfig({
    theme: {
      tokens: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        colors: staticColors as any,
        fonts: {
          heading: { value: "Lato" },
          body: { value: "Lato" },
        },
        fontSizes: fontSizeTokens,
        spacing: spacingTokens,
        sizes: sizeTokens,
        radii: radiiTokens,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      semanticTokens: customSemanticTokens as any,
      textStyles,
      breakpoints: breakpointsTheme,
      recipes: {
        link: linkRecipe,
      },
      slotRecipes: {
        accordion: accordionSlotRecipe,
        table: tableSlotRecipe,
        checkbox: checkboxSlotRecipe,
        tag: tagSlotRecipe,
      },
    },
    globalCss: {
      ":root": { "--100dvh": "90vh" },
      "@supports (height: 100dvh)": {
        ":root": { "--100dvh": "100dvh" },
      },
      body: {
        color: "fg",
        fontSize: "md", // 16px - default for all text
      },
      ...selectionStyles,
    },
  } as Parameters<typeof defineConfig>[0]);

  return createSystem(defaultConfig, customConfig);
}
